import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Plus, Pencil, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";

export default function RMVendors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await api.get("/production/rm-vendors", { params });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search]);

  const remove = async (v) => {
    if (!window.confirm(`Deactivate vendor "${v.name}"?`)) return;
    try {
      await api.delete(`/production/rm-vendors/${v.id}`);
      toast.success("Vendor deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input placeholder="Search vendors…" className="pl-8 h-9 text-sm bg-white" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="rmv-search" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">{items.length} vendor{items.length !== 1 ? "s" : ""}</span>
          <Button className="h-9 bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setEditing({ __new: true, name: "", address: "", email: "", mobile: "", gst_number: "", pan_number: "", district: "", state: "", country: "India" })} data-testid="rmv-add-btn">
            <Plus className="h-4 w-4" /> Add Vendor
          </Button>
        </div>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-200px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Mobile</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5">GST</th>
                <th className="px-4 py-2.5">PAN</th>
                <th className="px-4 py-2.5">City / State</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                  <Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No vendors yet — click <span className="font-semibold text-gray-700">Add Vendor</span>
                </td></tr>
              )}
              {items.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50" data-testid={`rmv-row-${v.id}`}>
                  <td className="px-4 py-2 font-medium text-gray-900">{v.name}</td>
                  <td className="px-4 py-2 text-gray-600">{v.mobile || "—"}</td>
                  <td className="px-4 py-2 text-gray-600">{v.email || "—"}</td>
                  <td className="px-4 py-2 text-gray-600 font-mono text-xs">{v.gst_number || "—"}</td>
                  <td className="px-4 py-2 text-gray-600 font-mono text-xs">{v.pan_number || "—"}</td>
                  <td className="px-4 py-2 text-gray-600">{[v.district, v.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setEditing({ ...v })} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`rmv-edit-${v.id}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button onClick={() => remove(v)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-red-50 text-xs text-red-700" data-testid={`rmv-delete-${v.id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      {editing && (
        <VendorEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function VendorEditor({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    name: initial.name || "",
    address: initial.address || "",
    email: initial.email || "",
    mobile: initial.mobile || "",
    gst_number: initial.gst_number || "",
    pan_number: initial.pan_number || "",
    district: initial.district || "",
    state: initial.state || "",
    country: initial.country || "India",
    credit_period_days: initial.credit_period_days || 0,
    opening_balance: initial.opening_balance || 0,
    opening_balance_type: initial.opening_balance_type || "payable",
    opening_balance_date: initial.opening_balance_date || new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        credit_period_days: Number(form.credit_period_days) || 0,
        opening_balance: Number(form.opening_balance) || 0,
      };
      if (isNew) {
        await api.post("/production/rm-vendors", payload);
        toast.success("Vendor created");
      } else {
        await api.put(`/production/rm-vendors/${initial.id}`, payload);
        toast.success("Vendor updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Raw Material Vendor" : `Edit — ${initial.name}`}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *" className="col-span-2"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="rmv-name" /></Field>
          <Field label="Mobile"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="rmv-mobile" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="rmv-email" /></Field>
          <Field label="GST Number"><Input value={form.gst_number} onChange={(e) => set("gst_number", e.target.value.toUpperCase())} className="font-mono" data-testid="rmv-gst" /></Field>
          <Field label="PAN Number"><Input value={form.pan_number} onChange={(e) => set("pan_number", e.target.value.toUpperCase())} className="font-mono" data-testid="rmv-pan" /></Field>
          <Field label="District"><Input value={form.district} onChange={(e) => set("district", e.target.value)} data-testid="rmv-district" /></Field>
          <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="rmv-state" /></Field>
          <Field label="Country"><Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="rmv-country" /></Field>
          <Field label="Default Credit Period (Days)" className="col-span-2">
            <Input
              type="number"
              min="0"
              step="1"
              value={form.credit_period_days}
              onChange={(e) => set("credit_period_days", Number(e.target.value) || 0)}
              className="text-right tabular-nums max-w-[140px]"
              placeholder="0 = due immediately"
              data-testid="rmv-credit-days"
            />
            <p className="text-[11px] text-gray-500 mt-1">Every new GRN for this vendor will auto-fill this value. You can still override it per invoice.</p>
          </Field>
          <Field label="Address" className="col-span-2">
            <textarea className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 min-h-[70px]" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="rmv-address" />
          </Field>
          {/* Opening balance — feeds the vendor ledger. Editing this after
              some transactions have posted is safe: the backend replaces
              the single "opening" ledger row atomically. */}
          <div className="col-span-2 mt-1 rounded-md border border-amber-200 bg-amber-50/50 p-3">
            <p className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-2">Opening Balance (Vendor Ledger)</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Amount">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.opening_balance}
                  onChange={(e) => set("opening_balance", e.target.value)}
                  className="text-right tabular-nums"
                  data-testid="rmv-opening-amount"
                />
              </Field>
              <Field label="Type">
                <select
                  value={form.opening_balance_type}
                  onChange={(e) => set("opening_balance_type", e.target.value)}
                  className="h-10 w-full rounded-md border border-gray-200 px-2 text-sm"
                  data-testid="rmv-opening-type"
                >
                  <option value="payable">Payable (we owe vendor)</option>
                  <option value="receivable">Receivable (vendor owes us)</option>
                </select>
              </Field>
              <Field label="As of Date">
                <Input
                  type="date"
                  value={form.opening_balance_date}
                  onChange={(e) => set("opening_balance_date", e.target.value)}
                  data-testid="rmv-opening-date"
                />
              </Field>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={save} disabled={saving} data-testid="rmv-save">
            {saving ? "Saving…" : (isNew ? "Create Vendor" : "Save Changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
